// plan-modusign-style-acceptance §Phase1 — 서명 양식 목록 페이지.
// Route: /field/sign/templates (FIELD_MANAGER + ADMIN)

import { Fragment, useState, type CSSProperties } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import type { SignatureDocumentType, SignatureTemplateSummaryDto } from '@bhops/shared';
import { signatureTemplatesApi, type VersionCleanupReport } from '@/api/signatureTemplates';
import { extractApiError } from '@/api/client';
// 추가수정 2차(26-07-27) §2 — 표시 날짜는 KST 정본으로.
import { kstYmd } from '@/lib/format-date';
import { Button } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { SignTemplateNewModal } from './SignTemplateNewModal';

const DOC_TYPE_LABEL: Record<SignatureDocumentType, string> = {
  DELIVERY_RECEIPT: '인수증',
  INSTALLATION_TRAINING: '설치·교육 인수증',
  REMOTE_SERVICE_CONSENT: '데이터 수집·이용 동의서',
  CUSTOM: '사용자 정의',
};

const wrap: CSSProperties = { padding: '24px 28px', maxWidth: 1100 };
const header: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 };
const tableWrap: CSSProperties = {
  border: '1px solid var(--border-default)',
  borderRadius: 'var(--r-card)',
  overflow: 'hidden',
};
const th: CSSProperties = {
  background: 'var(--bg-surface)',
  borderBottom: '1px solid var(--border-subtle)',
  padding: '10px 14px',
  textAlign: 'left',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--text-secondary)',
};
const td: CSSProperties = {
  padding: '10px 14px',
  fontSize: 'var(--text-sm)',
  borderBottom: '1px solid var(--border-subtle)',
  verticalAlign: 'middle',
};
const rowBtn: CSSProperties = {
  marginRight: 8,
  fontSize: 12,
  padding: '3px 8px',
  borderRadius: 'var(--r-btn)',
  border: '1px solid var(--border-default)',
  cursor: 'pointer',
  background: 'var(--bg-surface)',
};

function statusPill(archived: boolean, activeCount: number) {
  if (archived)
    return (
      <span
        style={{
          background: 'var(--bg-sunken)',
          borderRadius: 'var(--r-pill)',
          padding: '2px 8px',
          fontSize: 'var(--text-xs)',
        }}
      >
        보관됨
      </span>
    );
  if (activeCount > 0)
    return (
      <span
        style={{
          background: 'var(--bg-info-subtle)',
          color: 'var(--fg-brand)',
          borderRadius: 'var(--r-pill)',
          padding: '2px 8px',
          fontSize: 'var(--text-xs)',
        }}
      >
        진행 중 {activeCount}건
      </span>
    );
  return (
    <span
      style={{
        background: 'var(--bg-success-subtle)',
        color: 'var(--fg-on-success-tint)',
        borderRadius: 'var(--r-pill)',
        padding: '2px 8px',
        fontSize: 'var(--text-xs)',
      }}
    >
      사용 가능
    </span>
  );
}

// FB §7 (결정 E) — 디자이너는 풀페이지 라우트라 드로어 안에서 직접 navigate 하면
// 디자이너가 stale 드로어 오버레이 아래에 마운트된다. 드로어를 소유한
// SignDocumentsPage 가 "드로어 닫고 → 풀페이지 이동" 을 수행하도록 콜백을 받는다.
// 독립 페이지(/field/sign/templates)로 쓰일 땐 prop 없이 자체 navigate fallback.
interface SignTemplatesPageProps {
  onOpenDesigner?: (templateId: string) => void;
}

export function SignTemplatesPage({ onOpenDesigner }: SignTemplatesPageProps = {}) {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();

  // 콜백이 있으면(드로어 안) 부모가 드로어를 닫고 이동. 없으면 자체 navigate.
  const goToDesigner = (templateId: string) => {
    if (onOpenDesigner) onOpenDesigner(templateId);
    else navigate(`/field/sign/templates/${templateId}/design`);
  };
  const [newOpen, setNewOpen] = useState(false);
  const [typeFilter, setTypeFilter] = useState<SignatureDocumentType | ''>('');
  // round5 #6e — (구) '보관된 양식 포함' 체크박스(기본 해제)는 보관 직후 행이
  // 화면에서 사라져 복원/완전삭제 버튼을 발견할 수 없게 만들었다(기능은
  // 완비, 발견성 문제). 세그먼트 탭(사용 중 | 보관됨 n)으로 교체 — 항상
  // includeArchived 로 한 번 조회 후 클라이언트에서 archivedAt 유무로 분리
  // (카운트 배지도 추가 쿼리 없이).
  const [view, setView] = useState<'active' | 'archived'>('active');
  const [archiveTarget, setArchiveTarget] = useState<SignatureTemplateSummaryDto | null>(null);
  // Round 2 Phase G (#5, 결정 D-6) — 보관 양식 복원 / FK 가드된 완전 삭제 대상.
  const [restoreTarget, setRestoreTarget] = useState<SignatureTemplateSummaryDto | null>(null);
  const [hardDeleteTarget, setHardDeleteTarget] = useState<SignatureTemplateSummaryDto | null>(
    null,
  );

  const q = useQuery({
    queryKey: ['signature-templates', { typeFilter, includeArchived: true }],
    queryFn: () =>
      signatureTemplatesApi.list({
        documentType: typeFilter || undefined,
        includeArchived: true,
      }),
  });

  const archiveMutation = useMutation({
    mutationFn: (id: string) => signatureTemplatesApi.archive(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-templates'] });
      // round5 #6e — 보관 직후 행이 '사용 중' 탭에서 사라지므로 복원 경로 안내.
      toast.success("양식을 보관했습니다. '보관됨' 탭에서 복원하거나 완전히 삭제할 수 있습니다.");
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // Round 2 Phase G (#5, 결정 D-6) — 보관 복원 / FK 가드된 완전 삭제.
  const restoreMutation = useMutation({
    mutationFn: (id: string) => signatureTemplatesApi.restore(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-templates'] });
      toast.success('양식이 복원되었습니다.');
      setRestoreTarget(null);
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  const hardDeleteMutation = useMutation({
    mutationFn: (id: string) => signatureTemplatesApi.hardDelete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['signature-templates'] });
      toast.success('양식을 완전히 삭제했습니다.');
      setHardDeleteTarget(null);
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // 개선계획 §5-1 — 양식 복사. 비슷한 양식을 처음부터 만들지 않도록.
  const duplicateMutation = useMutation({
    mutationFn: (id: string) => signatureTemplatesApi.duplicate(id),
    onSuccess: ({ id }) => {
      qc.invalidateQueries({ queryKey: ['signature-templates'] });
      toast.success('복사본이 만들어졌습니다. 이어서 편집하세요.');
      goToDesigner(id);
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // 개선계획 §5-2 — 버전 이력 펼치기 + [이 버전으로 복원].
  const [openHistoryId, setOpenHistoryId] = useState<string | null>(null);
  const versionsQ = useQuery({
    queryKey: ['signature-template-versions', openHistoryId],
    queryFn: () => signatureTemplatesApi.versions(openHistoryId!),
    enabled: !!openHistoryId,
  });
  const restoreVersionMutation = useMutation({
    mutationFn: (id: string) => signatureTemplatesApi.restoreVersion(id),
    onSuccess: (result) => {
      qc.invalidateQueries({ queryKey: ['signature-templates'] });
      qc.invalidateQueries({ queryKey: ['signature-template-versions'] });
      setOpenHistoryId(null);
      toast.success(`선택한 내용으로 새 버전(v${result.version})을 만들었습니다.`);
    },
    onError: (err) => toast.error(extractApiError(err)),
  });
  const [restoreVersionTarget, setRestoreVersionTarget] =
    useState<SignatureTemplateSummaryDto | null>(null);

  // 개선계획 §5-2 — 기존 중복 구버전 일괄 보관. 마이그레이션이 아니라 관리자
  // 실행형: 먼저 드라이런으로 무엇이 몇 건 보관되는지 보이고, 확인 후 실행.
  const [cleanupReport, setCleanupReport] = useState<VersionCleanupReport | null>(null);
  const cleanupPreviewMutation = useMutation({
    mutationFn: () => signatureTemplatesApi.previewVersionCleanup(),
    onSuccess: (report) => setCleanupReport(report),
    onError: (err) => toast.error(extractApiError(err)),
  });
  const cleanupApplyMutation = useMutation({
    mutationFn: () => signatureTemplatesApi.applyVersionCleanup(),
    onSuccess: (report) => {
      qc.invalidateQueries({ queryKey: ['signature-templates'] });
      setCleanupReport(null);
      toast.success(`구버전 ${report.rowCount}건을 보관 처리했습니다.`);
    },
    onError: (err) => toast.error(extractApiError(err)),
  });

  // round5 #6e — 탭별 분리(클라이언트). 카운트는 전체 응답 기준.
  const allRows: SignatureTemplateSummaryDto[] = q.data ?? [];
  const activeRows = allRows.filter((r) => !r.archivedAt);
  const archivedRows = allRows.filter((r) => !!r.archivedAt);
  const rows = view === 'active' ? activeRows : archivedRows;

  return (
    <div style={wrap}>
      <div style={header}>
        <h2 style={{ margin: 0, fontSize: 'var(--text-xl)', fontWeight: 700 }}>서명 양식 관리</h2>
        {/* 개선계획 §5-2 — 예전에 쌓인 중복 구버전 정리. 항상 드라이런 먼저. */}
        <Button
          variant="ghost"
          onClick={() => cleanupPreviewMutation.mutate()}
          disabled={cleanupPreviewMutation.isPending}
          style={{ marginLeft: 'auto' }}
        >
          {cleanupPreviewMutation.isPending ? '확인 중…' : '구버전 정리'}
        </Button>
        <Button onClick={() => setNewOpen(true)}>+ 새 양식</Button>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
        <select
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value as SignatureDocumentType | '')}
          style={{
            padding: '6px 10px',
            borderRadius: 'var(--r-input)',
            border: '1px solid var(--border-default)',
            fontSize: 'var(--text-sm)',
            // round5 #21 — UA 기본 흰 배경이 다크에서 떠 보임.
            background: 'var(--bg-surface)',
            color: 'var(--fg-default)',
          }}
        >
          <option value="">전체 문서 유형</option>
          {Object.entries(DOC_TYPE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>
              {v}
            </option>
          ))}
        </select>
        {/* round5 #6e — '사용 중 | 보관됨 (n)' 세그먼트 탭. */}
        <div
          role="tablist"
          style={{
            display: 'inline-flex',
            border: '1px solid var(--border-default)',
            borderRadius: 'var(--r-pill)',
            overflow: 'hidden',
            fontSize: 'var(--text-sm)',
          }}
        >
          {(
            [
              { key: 'active', label: `사용 중 (${activeRows.length})` },
              { key: 'archived', label: `보관됨 (${archivedRows.length})` },
            ] as const
          ).map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              aria-selected={view === t.key}
              onClick={() => setView(t.key)}
              style={{
                padding: '6px 14px',
                border: 'none',
                cursor: 'pointer',
                background: view === t.key ? 'var(--brand-subtle)' : 'var(--bg-surface)',
                color: view === t.key ? 'var(--fg-brand)' : 'var(--fg-default)',
                fontWeight: view === t.key ? 600 : 400,
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {q.isLoading && <p style={{ color: 'var(--text-secondary)' }}>불러오는 중…</p>}
      {q.isError && <p style={{ color: 'var(--color-error)' }}>{extractApiError(q.error)}</p>}

      {!q.isLoading && rows.length === 0 && (
        <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--text-secondary)' }}>
          <p>등록된 서명 양식이 없습니다.</p>
          <Button onClick={() => setNewOpen(true)}>첫 양식 만들기</Button>
        </div>
      )}

      {rows.length > 0 && (
        <div style={tableWrap}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>양식 이름</th>
                <th style={th}>문서 유형</th>
                <th style={th}>버전</th>
                <th style={th}>페이지</th>
                <th style={th}>상태</th>
                <th style={th}>생성자</th>
                <th style={th}>수정일</th>
                <th style={th}>작업</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                // Round 2 Phase G (#5) — 행 전체 클릭으로 디자이너 진입하던 버그
                // 제거. 명시적 "편집" 버튼으로만 이동(실수 진입 방지).
                // 개선계획 §5-2 — 한 행 = 한 양식(최신 버전). 옛 버전은 [이력]으로 펼친다.
                <Fragment key={r.id}>
                  <tr>
                    <td style={td}>
                      <span style={{ fontWeight: 600 }}>{r.name}</span>
                      {r.allowAttachment && (
                        <span
                          style={{
                            marginLeft: 6,
                            fontSize: 'var(--text-xs)',
                            padding: '1px 6px',
                            borderRadius: 'var(--r-pill)',
                            border: '1px solid var(--border-default)',
                            color: 'var(--text-secondary)',
                          }}
                        >
                          첨부 사용
                        </span>
                      )}
                    </td>
                    <td style={td}>{DOC_TYPE_LABEL[r.documentType]}</td>
                    <td style={td}>v{r.version}</td>
                    <td style={td}>{r.pageCount}p</td>
                    <td style={td}>{statusPill(!!r.archivedAt, r.activeDocumentCount)}</td>
                    <td style={td}>{r.createdByName}</td>
                    {/* 추가수정 2차(26-07-27) §2 — ISO 를 그대로 자르면 UTC 날짜.
                        KST 로 환산하고 목록이므로 날짜만(결정 ②A). */}
                    <td style={td}>{kstYmd(r.updatedAt) ?? '–'}</td>
                    <td
                      style={{ ...td, whiteSpace: 'nowrap' }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button onClick={() => goToDesigner(r.id)} style={rowBtn}>
                        편집
                      </button>
                      <button
                        onClick={() => duplicateMutation.mutate(r.id)}
                        disabled={duplicateMutation.isPending}
                        style={rowBtn}
                      >
                        복사
                      </button>
                      <button
                        onClick={() => setOpenHistoryId(openHistoryId === r.id ? null : r.id)}
                        aria-expanded={openHistoryId === r.id}
                        style={{ ...rowBtn, color: 'var(--text-secondary)' }}
                      >
                        {openHistoryId === r.id ? '이력 닫기' : '이력'}
                      </button>
                      {!r.archivedAt ? (
                        <button
                          onClick={() => setArchiveTarget(r)}
                          style={{
                            fontSize: 12,
                            padding: '3px 8px',
                            borderRadius: 'var(--r-btn)',
                            border: '1px solid var(--border-default)',
                            cursor: 'pointer',
                            background: 'var(--bg-surface)',
                            color: 'var(--text-secondary)',
                          }}
                        >
                          보관
                        </button>
                      ) : (
                        // Round 2 Phase G (#5, D-6) — 보관된 양식: 복원 + FK 가드된 완전 삭제.
                        <>
                          <button
                            onClick={() => setRestoreTarget(r)}
                            style={{
                              fontSize: 12,
                              padding: '3px 8px',
                              borderRadius: 'var(--r-btn)',
                              border: '1px solid var(--border-default)',
                              cursor: 'pointer',
                              background: 'var(--bg-surface)',
                              color: 'var(--text-secondary)',
                            }}
                          >
                            복원
                          </button>
                          <button
                            onClick={() => setHardDeleteTarget(r)}
                            style={{
                              marginLeft: 8,
                              fontSize: 12,
                              padding: '3px 8px',
                              borderRadius: 'var(--r-btn)',
                              border: '1px solid var(--border-default)',
                              cursor: 'pointer',
                              background: 'var(--bg-surface)',
                              color: 'var(--color-error)',
                            }}
                          >
                            완전삭제
                          </button>
                        </>
                      )}
                    </td>
                  </tr>
                  {openHistoryId === r.id && (
                    <tr>
                      <td style={{ ...td, background: 'var(--bg-sunken)' }} colSpan={8}>
                        {versionsQ.isLoading && (
                          <span
                            style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}
                          >
                            이력 불러오는 중…
                          </span>
                        )}
                        {versionsQ.isError && (
                          <span style={{ color: 'var(--color-error)', fontSize: 'var(--text-xs)' }}>
                            {extractApiError(versionsQ.error)}
                          </span>
                        )}
                        {versionsQ.data && versionsQ.data.length <= 1 && (
                          <span
                            style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-xs)' }}
                          >
                            이전 버전 없음
                          </span>
                        )}
                        {versionsQ.data && versionsQ.data.length > 1 && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                            {versionsQ.data.map((v) => (
                              <div
                                key={v.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 10,
                                  fontSize: 'var(--text-xs)',
                                }}
                              >
                                <span style={{ fontWeight: 600, minWidth: 44 }}>v{v.version}</span>
                                <span style={{ color: 'var(--text-secondary)' }}>
                                  {/* 추가수정 2차(26-07-27) §2 — 버전 이력 날짜도 KST. */}
                                  {kstYmd(v.createdAt) ?? '–'} · {v.createdByName} · {v.pageCount}p
                                </span>
                                {v.isLatestInLineage ? (
                                  <span style={{ color: 'var(--fg-brand)' }}>현재 사용 중</span>
                                ) : (
                                  <button
                                    onClick={() => setRestoreVersionTarget(v)}
                                    style={{ ...rowBtn, marginRight: 0 }}
                                  >
                                    이 버전으로 복원
                                  </button>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {newOpen && (
        <SignTemplateNewModal
          onClose={() => setNewOpen(false)}
          onCreated={(id) => {
            setNewOpen(false);
            qc.invalidateQueries({ queryKey: ['signature-templates'] });
            // FB §7 — 드로어 안이면 부모가 드로어를 닫고 풀페이지 이동.
            goToDesigner(id);
          }}
        />
      )}

      <ConfirmDialog
        open={!!archiveTarget}
        title="양식 보관"
        message={`"${archiveTarget?.name}" 양식을 보관하시겠습니까?`}
        confirmLabel="보관"
        danger
        busy={archiveMutation.isPending}
        onConfirm={() => {
          if (archiveTarget) archiveMutation.mutate(archiveTarget.id);
          setArchiveTarget(null);
        }}
        onCancel={() => setArchiveTarget(null)}
      />

      <ConfirmDialog
        open={!!restoreTarget}
        title="양식 복원"
        message={`"${restoreTarget?.name}" 양식을 복원하시겠습니까?`}
        confirmLabel="복원"
        busy={restoreMutation.isPending}
        onConfirm={() => {
          if (restoreTarget) restoreMutation.mutate(restoreTarget.id);
        }}
        onCancel={() => {
          if (!restoreMutation.isPending) setRestoreTarget(null);
        }}
      />

      <ConfirmDialog
        open={!!hardDeleteTarget}
        title="양식 완전 삭제"
        message={`"${hardDeleteTarget?.name}" 양식을 완전히 삭제하시겠습니까? 되돌릴 수 없습니다. 이 양식을 쓰는 서명 문서가 있으면 삭제되지 않습니다.`}
        confirmLabel="완전 삭제"
        danger
        busy={hardDeleteMutation.isPending}
        onConfirm={() => {
          if (hardDeleteTarget) hardDeleteMutation.mutate(hardDeleteTarget.id);
        }}
        onCancel={() => {
          if (!hardDeleteMutation.isPending) setHardDeleteTarget(null);
        }}
      />

      {/* 개선계획 §5-2 — 과거 버전 내용으로 '새 버전'을 만든다(과거 행은 그대로 보존). */}
      <ConfirmDialog
        open={!!restoreVersionTarget}
        title="이 버전으로 복원"
        message={`v${restoreVersionTarget?.version ?? ''}의 내용으로 새 버전을 만듭니다. 지금 사용 중인 버전은 보관되고, 진행 중인 서명 문서는 원래 버전을 그대로 씁니다.`}
        confirmLabel="새 버전 만들기"
        busy={restoreVersionMutation.isPending}
        onConfirm={() => {
          if (restoreVersionTarget) restoreVersionMutation.mutate(restoreVersionTarget.id);
          setRestoreVersionTarget(null);
        }}
        onCancel={() => {
          if (!restoreVersionMutation.isPending) setRestoreVersionTarget(null);
        }}
      />

      {/* 개선계획 §5-2 — 구버전 일괄 보관: 드라이런 결과를 먼저 보여주고 확인받는다. */}
      <ConfirmDialog
        open={!!cleanupReport}
        title="구버전 정리"
        message={
          cleanupReport && cleanupReport.rowCount === 0
            ? '정리할 구버전이 없습니다.'
            : `양식 ${cleanupReport?.lineageCount ?? 0}종의 구버전 ${cleanupReport?.rowCount ?? 0}건을 보관 처리합니다. ` +
              `보관해도 진행 중인 서명 문서는 그대로 진행되며, 필요하면 이력에서 다시 복원할 수 있습니다.\n\n` +
              (cleanupReport?.items ?? [])
                .slice(0, 12)
                .map(
                  (it) =>
                    `· ${it.name}: v${it.archiveVersions.join(', v')} 보관, v${it.keepVersion} 유지`,
                )
                .join('\n') +
              ((cleanupReport?.items.length ?? 0) > 12
                ? `\n… 외 ${(cleanupReport?.items.length ?? 0) - 12}종`
                : '')
        }
        confirmLabel={cleanupReport && cleanupReport.rowCount === 0 ? '닫기' : '보관 실행'}
        busy={cleanupApplyMutation.isPending}
        onConfirm={() => {
          if (cleanupReport && cleanupReport.rowCount === 0) setCleanupReport(null);
          else cleanupApplyMutation.mutate();
        }}
        onCancel={() => {
          if (!cleanupApplyMutation.isPending) setCleanupReport(null);
        }}
      />
    </div>
  );
}
